'use client';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { MediaType } from '@repo/ui/src/types/image';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { useRef, useEffect, useState } from 'react';
import { ToutContent } from '@repo/ui/src/components/ToutContent/_toutContent';
import { classNames } from '@repo/ui/src/utils/classNames';
import { appConfig } from '../../utils/appConfig';
import BackgroundVideo from '@repo/ui/src/components/BackgroundVideo';

interface CardProps {
  img: {
    image: MediaType;
    altTag: string;
  };
  toutContent: ToutContentProps;
  largeCard?: boolean;
  reverse?: boolean;
  isMeaterMobile?: boolean;
  video?: MediaType;
  index: number;
}

function getGridPosition(index: number, reverse: boolean): string {
  const n = index + 1;

  if (reverse) {
    switch (n) {
      case 1:
        return 'col-start-1 col-end-3 row-start-1 row-end-3';
      case 4:
        return 'col-start-2 col-end-4 row-start-3 row-end-5 lg:col-start-auto lg:col-end-auto lg:row-start-auto lg:row-end-auto';
      case 6:
        return 'col-start-3 col-end-5';
      case 7:
        return 'col-start-2 col-end-3';
      case 8:
        return 'col-start-1 col-end-2 row-start-3 row-end-4';
      case 9:
        return 'col-start-2 col-end-3 row-start-4 row-end-5';
      default:
        return '';
    }
  }

  switch (n) {
    case 1:
      return 'col-start-2 col-end-4 row-start-1 row-end-3 lg:col-start-3 lg:col-end-5 lg:row-start-1 lg:row-end-3';
    case 4:
      return 'col-start-1 col-end-3 row-start-3 row-end-5 lg:col-start-auto lg:col-end-auto lg:row-start-auto lg:row-end-auto';
    case 6:
      return 'lg:col-start-1 lg:col-end-3 lg:row-start-3 lg:row-end-5';
    case 8:
      return 'lg:row-start-3 lg:row-end-4';
    default:
      return '';
  }
}

const GridCard = ({ img, toutContent, largeCard, reverse, isMeaterMobile, video, index }: CardProps) => {
  const ref = useRef<any>(null);
  const [translateHeight, setHeight] = useState(0);
  const imageUrl = buildImageSrc(img.image, 700, 1, 1);

  useEffect(() => {
    const element = ref.current;
    if (element) {
      const bottomContent = element.querySelector('.bottom-content');
      const bottomContentHeight = bottomContent?.clientHeight;
      if (bottomContentHeight) {
        setHeight(bottomContentHeight);
      }
    }
  }, []);

  if (isMeaterMobile) {
    return (
      <li className="relative w-full aspect-square overflow-hidden" tabIndex={0}>
        {video ? (
          <BackgroundVideo
            video={video}
            width={700}
            fillContainer
            className="absolute inset-0 h-full"
            playButtonPosition="right"
          />
        ) : (
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-[rgba(16,16,16,1)] to-[rgba(0,0,0,0)]" />
        <div className="absolute bottom-0 left-0 w-full p-5">
          <ToutContent
            dark
            headingStyleVariant="h5"
            heading={toutContent.heading}
            {...toutContent}
            largeCard={largeCard}
            align="left"
            divider
            buttonSize="small"
          />
        </div>
      </li>
    );
  }

  return (
    <li
      className={classNames(
        'group/card accCard relative w-full h-full object-cover overflow-y-hidden grid grid-cols-1',
        'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-brand-300',
        getGridPosition(index, !!reverse),
      )}
      ref={ref}
      tabIndex={0}
      style={{
        ['--translate-height' as any]: `${translateHeight + 4}px`,
        ['--tout-offset' as any]: translateHeight > 0 ? '2.5rem' : '0px',
      }}
    >
      {video ? (
        <div className="row-start-1 col-start-1 w-full aspect-square">
          <BackgroundVideo
            video={video}
            width={700}
            fillContainer
            className="h-full"
            playButtonPosition="right"
          />
        </div>
      ) : (
        <img
          src={imageUrl}
          alt=""
          className="w-full h-auto object-cover row-start-1 col-start-1 aspect-square"
        />
      )}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-[rgba(16,16,16,1)] to-[rgba(0,0,0,0)]" />
      <div
        className={classNames(
          'tout flex px-5 flex-col justify-end row-start-1 col-start-1 w-full h-full transition-transform duration-[600ms]',
          '[transform:translateY(var(--tout-offset,0px))]',
          'group-hover/card:[transform:translateY(calc(var(--translate-height)_*_-1))]',
          'group-focus/card:[transform:translateY(calc(var(--translate-height)_*_-1))]',
          '[&_.bottom-content]:opacity-0 [&_.bottom-content]:absolute [&_.bottom-content]:pr-5 [&_.bottom-content]:transition-all [&_.bottom-content]:duration-[800ms] [&_.bottom-content]:ease-out',
          'group-hover/card:[&_.bottom-content]:opacity-100 group-focus/card:[&_.bottom-content]:opacity-100',
        )}
      >
        <ToutContent
          dark
          headingStyleVariant={appConfig.isTraeger ? 'h5' : 'h3'}
          heading={toutContent.heading}
          {...toutContent}
          largeCard={largeCard}
          align="left"
          divider
          buttonSize="small"
          className={appConfig.isMeater && '[&_h2]:uppercase'}
        />
      </div>
    </li>
  );
};

export default GridCard;
